import React, { forwardRef, useEffect, useImperativeHandle, useRef, } from 'react';
import { Animated, PanResponder, Platform, StyleSheet, View, } from 'react-native';
import { useSlideBack } from '../../hooks/useSlideBack';
import { ScreenWidth } from '../../utils/const';
import { useSwipeableStatus } from './context';
/** We offer a special kind of ListItem which is swipeable from both ends and allows users select an event. */
export const Swipeable = forwardRef((props, ref) => {
    const { style, children, leftStyle, rightStyle, leftContent, rightContent, leftWidth = 0, rightWidth = ScreenWidth / 3, minSlideWidth: minSlideWidthOuter, animation = { type: 'spring', duration: 200 }, onSwipeBegin, onSwipeEnd, onWillClose, onWillOpen, onOpened, onClosed, } = props;
    const minSlideWidth = minSlideWidthOuter !== null && minSlideWidthOuter !== void 0 ? minSlideWidthOuter : (rightWidth !== null && rightWidth !== void 0 ? rightWidth : leftWidth) / 3;
    const translateX = React.useRef(new Animated.Value(0));
    const panX = React.useRef(0);
    const uid = useRef(`swipeable_id_${Math.random()}`).current;
    const { addStatusRef, setCurStatus } = useSwipeableStatus();
    const { setSlideBack } = useSlideBack();
    const slideAnimation = React.useCallback((toValue) => {
        if (toValue === 0) {
            onWillClose === null || onWillClose === void 0 ? void 0 : onWillClose();
        }
        else {
            setCurStatus({ uid, isOpened: true });
            onWillOpen === null || onWillOpen === void 0 ? void 0 : onWillOpen();
            setSlideBack(false);
        }
        panX.current = toValue;
        Animated[animation.type || 'spring'](translateX.current, {
            toValue,
            useNativeDriver: Platform.OS !== 'web',
            duration: animation.duration || 200,
        }).start(({ finished }) => {
            if (finished) {
                if (toValue === 0) {
                    onClosed === null || onClosed === void 0 ? void 0 : onClosed();
                }
                else {
                    onOpened === null || onOpened === void 0 ? void 0 : onOpened();
                }
            }
        });
    }, [animation.duration, animation.type]);
    const resetCallBack = React.useCallback(() => {
        slideAnimation(0);
    }, [slideAnimation]);
    useEffect(() => {
        addStatusRef({
            ref: {
                close: resetCallBack,
            },
            uid,
        });
    }, []);
    useImperativeHandle(ref, () => {
        return {
            close: resetCallBack,
        };
    }, [resetCallBack]);
    const onMove = React.useCallback((_, { dx }) => {
        // 右侧内容时临界值判断
        if (rightContent) {
            if (panX.current >= rightWidth && dx < 0)
                return;
            if (-panX.current === rightWidth) {
                if (dx < 0)
                    return;
                if (dx > rightWidth) {
                    translateX.current.setValue(0);
                    return;
                }
            }
            if (panX.current === 0 && dx > 0) {
                if (dx > rightWidth) {
                    translateX.current.setValue(0);
                }
            }
            if (panX.current === 0 && (dx > 0 || Math.abs(dx) > rightWidth))
                return;
        }
        translateX.current.setValue(panX.current + dx);
    }, []);
    const onRelease = React.useCallback((_, { dx }) => {
        if (Math.abs(panX.current + dx) >= minSlideWidth) {
            slideAnimation(panX.current + dx > 0 ? leftWidth : -rightWidth);
        }
        else {
            slideAnimation(0);
        }
    }, [leftWidth, rightWidth, slideAnimation, minSlideWidth]);
    const shouldSlide = React.useCallback((_, { dx, dy, vx, vy }) => {
        if (dx > 0 && !leftContent && !panX.current) {
            return false;
        }
        if (dx < 0 && !rightContent && !panX.current) {
            return false;
        }
        return (Math.abs(dx) > Math.abs(dy) * 2 &&
            Math.abs(vx) > Math.abs(vy) * 2.5);
    }, [leftContent, rightContent]);
    const _panResponder = React.useMemo(() => PanResponder.create({
        onMoveShouldSetPanResponder: shouldSlide,
        onPanResponderGrant: (_event, { vx }) => {
            onSwipeBegin === null || onSwipeBegin === void 0 ? void 0 : onSwipeBegin(vx > 0 ? 'left' : 'right');
        },
        onPanResponderMove: onMove,
        onPanResponderRelease: onRelease,
        onPanResponderReject: onRelease,
        onPanResponderTerminate: onRelease,
        onPanResponderEnd: () => {
            onSwipeEnd === null || onSwipeEnd === void 0 ? void 0 : onSwipeEnd();
        },
    }), [onMove, onRelease, onSwipeBegin, onSwipeEnd, shouldSlide]);
    return (React.createElement(View, { style: [styles.container, style] },
        React.createElement(View, { style: [styles.actions, [{ height: '100%' }]] },
            React.createElement(View, { style: [
                    {
                        width: leftWidth,
                        zIndex: 1,
                    },
                    leftStyle,
                ] }, typeof leftContent === 'function'
                ? leftContent(resetCallBack)
                : leftContent),
            React.createElement(View, { style: styles.empty }),
            React.createElement(Animated.View, { style: [
                    {
                        width: rightWidth,
                        transform: [
                            {
                                translateX: translateX.current.interpolate({
                                    inputRange: [-rightWidth, 0],
                                    outputRange: [0, rightWidth],
                                }),
                            },
                        ],
                        zIndex: 1,
                    },
                    rightStyle,
                ] }, typeof rightContent === 'function'
                ? rightContent(resetCallBack)
                : rightContent)),
        React.createElement(Animated.View, Object.assign({ style: {
                height: '100%',
                transform: [
                    {
                        translateX: translateX.current,
                    },
                ],
            } }, _panResponder.panHandlers), children)));
});
const styles = StyleSheet.create({
    actions: {
        bottom: 0,
        left: 0,
        overflow: 'hidden',
        position: 'absolute',
        right: 0,
        top: 0,
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    container: {
        justifyContent: 'center',
    },
    empty: {
        flex: 0,
    },
});
